import React, { useState, useMemo } from 'react'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  Typography,
  TextField,
  Chip,
  Button,
  CircularProgress
} from '@mui/material'
import { useBookers } from '../../hooks/api/useLoadForms'

interface BookerSelectorProps {
  selectedBookerIds: string[]
  onSelectionChange: (bookerIds: string[]) => void
  error?: string
  disabled?: boolean
}

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

const BookerSelector: React.FC<BookerSelectorProps> = ({
  selectedBookerIds,
  onSelectionChange,
  error,
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const { data: bookersData, isLoading, error: bookersError } = useBookers()

  const bookers = bookersData?.results || []

  // Filter bookers based on search term
  const filteredBookers = useMemo(() => {
    if (!searchTerm) return bookers
    return bookers.filter(booker =>
      booker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booker.email.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [bookers, searchTerm])

  const handleSelectAll = () => {
    if (selectedBookerIds.length === filteredBookers.length) {
      // If all are selected, deselect all
      onSelectionChange([])
    } else {
      // Select all filtered bookers
      const allFilteredIds = filteredBookers.map(booker => booker._id)
      onSelectionChange(allFilteredIds)
    }
  }

  const handleBookerToggle = (bookerId: string) => {
    const newSelection = selectedBookerIds.includes(bookerId)
      ? selectedBookerIds.filter(id => id !== bookerId)
      : [...selectedBookerIds, bookerId]
    
    onSelectionChange(newSelection)
  }


  const isAllSelected = selectedBookerIds.length === filteredBookers.length && filteredBookers.length > 0
  const isIndeterminate = selectedBookerIds.length > 0 && selectedBookerIds.length < filteredBookers.length

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" gap={2}>
        <CircularProgress size={20} />
        <Typography>Loading bookers...</Typography>
      </Box>
    )
  }

  if (bookersError) {
    return (
      <Typography color="error">
        Error loading bookers. Please try again.
      </Typography>
    )
  }

  return (
    <FormControl fullWidth error={!!error} disabled={disabled}>
      <InputLabel id="booker-select-label">Select Order Booker</InputLabel>
      <Select
        labelId="booker-select-label"
        multiple
        value={selectedBookerIds}
        onChange={() => {}} // Handled by individual checkboxes
        input={<OutlinedInput label="Select Order Booker" />}
        renderValue={() => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedBookerIds.length === 0 ? (
              <Typography color="text.secondary">No bookers selected</Typography>
            ) : selectedBookerIds.length <= 3 ? (
              selectedBookerIds.map(bookerId => {
                const booker = bookers.find(b => b._id === bookerId)
                return (
                  <Chip
                    key={bookerId}
                    label={booker?.name || 'Unknown'}
                    size="small"
                    onDelete={() => handleBookerToggle(bookerId)}
                  />
                )
              })
            ) : (
              <Chip
                label={`${selectedBookerIds.length} bookers selected`}
                size="small"
              />
            )}
          </Box>
        )}
        MenuProps={MenuProps}
      >
        {/* Search field */}
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Filter: Enter keywords"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        </Box>

        {/* Select All / Unselect All */}
        <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Button
            size="small"
            onClick={handleSelectAll}
            disabled={filteredBookers.length === 0}
          >
            {isAllSelected ? 'Uncheck all' : 'Check all'}
          </Button>
          <Typography variant="caption" sx={{ ml: 1 }}>
            {selectedBookerIds.length} out of {filteredBookers.length} selected
          </Typography>
        </Box>

        {/* Booker list */}
        {filteredBookers.map((booker) => (
          <MenuItem
            key={booker._id}
            value={booker._id}
            onClick={() => handleBookerToggle(booker._id)}
          >
            <Checkbox
              checked={selectedBookerIds.includes(booker._id)}
              indeterminate={isIndeterminate && selectedBookerIds.includes(booker._id)}
            />
            <ListItemText
              primary={`${booker._id} [${booker.name}]`}
              secondary={booker.email}
            />
          </MenuItem>
        ))}

        {filteredBookers.length === 0 && searchTerm && (
          <MenuItem disabled>
            <Typography color="text.secondary">No bookers found</Typography>
          </MenuItem>
        )}
      </Select>
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </FormControl>
  )
}

export default BookerSelector
